import { MachineTable } from "@/components/machine-table";
import { useAuth } from "@/contexts/auth-context";
import { useMachines } from "@/hooks/use-machines";
import { usePageTitle } from "@/hooks/use-page-title";

function formatIdleDuration(idleSince: string | null): string {
  if (!idleSince) return "\u2014";
  const ms = Date.now() - new Date(idleSince).getTime();
  if (ms < 0) return "\u2014";
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 1) return "<1m";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function MyMachinesPage() {
  usePageTitle("My Machines");
  const { user } = useAuth();
  const { data: allMachines = [], isLoading } = useMachines();

  const myMachines = allMachines.filter((machine) => machine.allocated_to === user?.id);

  const totalGpus = myMachines.reduce((sum, machine) => sum + machine.gpu_count, 0);
  const gpuBreakdown = myMachines.reduce(
    (acc, machine) => {
      const key = machine.gpu_type;
      acc[key] = (acc[key] ?? 0) + machine.gpu_count;
      return acc;
    },
    {} as Record<string, number>,
  );
  const gpuBreakdownStr = Object.entries(gpuBreakdown)
    .map(([type, count]) => `${count}\u00d7 ${type}`)
    .join(" \u00b7 ");

  const idleMachines = myMachines
    .filter((machine) => machine.idle_since)
    .sort((machineA, machineB) => {
      // biome-ignore lint/style/noNonNullAssertion: filtered above
      const aTime = new Date(machineA.idle_since!).getTime();
      // biome-ignore lint/style/noNonNullAssertion: filtered above
      const bTime = new Date(machineB.idle_since!).getTime();
      return aTime - bTime;
    });
  const longestIdle = idleMachines[0] ?? null;

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-1 gap-3 px-4 pt-4 pb-3 sm:grid-cols-3 sm:gap-4 sm:px-6 sm:pt-5 sm:pb-4">
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Allocated to me
          </div>
          <div className="mt-1 font-mono text-3xl font-bold text-foreground">
            {myMachines.length}
          </div>
          <div className="mt-0.5 text-[12px] text-muted-foreground">
            {myMachines.filter((machine) => machine.fleet_managed).length} pool
            {" \u00b7 "}
            {myMachines.filter((machine) => !machine.fleet_managed).length} self-created
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Total GPUs
          </div>
          <div className="mt-1 font-mono text-3xl font-bold text-foreground">{totalGpus}</div>
          <div className="mt-0.5 text-[12px] text-muted-foreground">
            {gpuBreakdownStr || "\u2014"}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Longest idle
          </div>
          <div className="mt-1 font-mono text-3xl font-bold text-amber-600 dark:text-amber-400">
            {longestIdle ? formatIdleDuration(longestIdle.idle_since) : "\u2014"}
          </div>
          <div className="mt-0.5 text-[12px] text-muted-foreground">
            {longestIdle?.name ?? "No idle machines"}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <span className="text-sm text-muted-foreground">Loading machines...</span>
        </div>
      ) : (
        <MachineTable
          machines={myMachines}
          currentUserId={user?.id ?? 0}
          isAdmin={user?.is_admin}
          showSshString
          defaultSortKey="cloud_status"
        />
      )}
    </div>
  );
}
